// Base API configuration and utilities
// Always use relative URLs so the proxy in setupProxy.js can handle the routing
// This ensures the proxy can add authentication headers and route to the correct backend
import { apiLogger as logger } from '../utils/logger';

const API_BASE_URL = '/api'; // Always use relative URL so proxy works

// Note: Authentication is now handled via httpOnly cookies
// No need to manually set Authorization headers

// Request blocking mechanism to prevent certain requests during specific operations
export const blockedRequests = {
  // Map of endpoint patterns to block
  patterns: new Set(),
  // Block a specific endpoint pattern (supports exact match or regex pattern)
  blockEndpoint: (pattern) => {
    logger.debug(`Blocking requests to: ${pattern}`);
    blockedRequests.patterns.add(pattern);
    // Return a function to unblock
    return () => {
      logger.debug(`Unblocking requests to: ${pattern}`);
      blockedRequests.patterns.delete(pattern);
    };
  },
  // Check if a request to an endpoint should be blocked
  isBlocked: (endpoint, method) => {
    for (const pattern of blockedRequests.patterns) {
      if (endpoint.includes(pattern)) {
        logger.debug(`Request ${method} ${endpoint} blocked by pattern: ${pattern}`);
        return true;
      }
    }
    return false;
  }
};

logger.debug('Using API base URL:', API_BASE_URL);

// Helper function to handle API responses
const handleResponse = async (response) => {
  // Get the content type to check if we're getting JSON or HTML
  const contentType = response.headers.get('content-type');

  // Debug the response details
  logger.debug(`API Response: ${response.status} ${response.statusText}`);
  logger.debug(`Content-Type: ${contentType}`);
  logger.debug(`Response URL: ${response.url}`);

  if (contentType && contentType.indexOf('application/json') !== -1) {
    // Process as JSON
    try {
      const data = await response.json();
      logger.debug('JSON Response data:', data);
      
      if (!response.ok) {
        const errorMsg = (data && data.message) || response.statusText;
        logger.error(`API Error (${response.status}):`, errorMsg, data);
        return Promise.reject(new Error(`API Error (${response.status}): ${errorMsg}`));
      }
      
      return data;
    } catch (jsonError) {
      logger.error('Failed to parse JSON response:', jsonError);
      return Promise.reject(new Error(`Failed to parse JSON: ${jsonError.message}`));
    }
  } else {
    // Not JSON, probably HTML error page
    const text = await response.text();
    logger.error('Received non-JSON response:', text.substring(0, 300) + '...');
    
    // Check if backend is not running or endpoint doesn't exist
    if (response.status === 404) {
      logger.error('404 Error: Endpoint not found');
      return Promise.reject(new Error(
        `Backend endpoint not found: ${response.url}. Check if the backend server is running and accessible.`
      ));
    }

    // Check for authentication errors
    if (response.status === 403) {
      logger.error('403 Error: Forbidden access');
      return Promise.reject(new Error(
        `Server returned non-JSON response (403): Forbidden`
      ));
    }
    
    // Special case - if the response is empty but successful, return an empty success result
    if (response.ok && text.trim() === '') {
      logger.debug('Empty but successful response');
      return { success: true, data: [] };
    }
    
    // Special handling for Schema Registry CORS issues (403 Forbidden)
    if (response.status === 403 && response.url.includes('kafka') && text.includes('CORS')) {
      logger.warn('Detected Schema Registry CORS issue (403 Forbidden)');
      
      // For schemas endpoint, return empty array with status
      if (response.url.includes('/schemas')) {
        return { 
          data: [], 
          status: 'unavailable', 
          message: 'Schema Registry access forbidden (CORS issue)'
        };
      }
      
      // For schema/topic endpoint, return default schema
      if (response.url.includes('/schema/')) {
        const urlParts = response.url.split('/');
        const topicName = urlParts[urlParts.length - 1];
        return { 
          data: {
            id: `${topicName}-value`,
            topic: topicName,
            type: "JSON",
            version: 0,
            schema: "{\n  \"type\": \"object\",\n  \"properties\": {\n    \"message\": { \"type\": \"string\" }\n  }\n}"
          },
          status: 'unavailable',
          message: 'Schema Registry access forbidden (CORS issue)'
        };
      }
    }
    
    const error = `Server returned non-JSON response (${response.status}): ${response.statusText}`;
    logger.error(error, { responseText: text.substring(0, 500) });
    return Promise.reject(new Error(error));
  }
};

// Generic API methods
export const apiGet = async (endpoint, options = {}) => {
  try {
    // Check if this request should be blocked
    if (blockedRequests.isBlocked(endpoint, 'GET')) {
      logger.debug(`GET request to ${endpoint} was blocked - returning empty result`);
      // Return an empty result based on the endpoint type
      if (endpoint.includes('/topics')) {
        return { data: [] };
      } else if (endpoint.includes('/integrations')) {
        return [];
      } else if (endpoint.includes('/cluster/health')) {
        return { status: 'available', brokers: 1, topics: 0, partitions: 0 };
      } else {
        return [];
      }
    }
    
    // Remove leading slash from endpoint if it exists to prevent double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    logger.debug(`Making GET request to: ${API_BASE_URL}/${cleanEndpoint}`);
    
    // Add a timeout to prevent hanging requests
    const controller = new AbortController();
    // Use longer timeout for pipeline operations, settings (multiple contexts load simultaneously), and others
    let defaultTimeout = 5000;
    if (endpoint.includes('pipelines')) {
      defaultTimeout = 30000; // 30 seconds for pipeline operations
    } else if (endpoint.includes('settings')) {
      defaultTimeout = 15000; // 15 seconds for settings (multiple contexts may load simultaneously)
    }
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || defaultTimeout);
    
    try {
      const response = await fetch(`${API_BASE_URL}/${cleanEndpoint}`, {
        signal: controller.signal,
        credentials: 'include',  // Include httpOnly cookies in request
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      clearTimeout(timeoutId);

      logger.debug(`Received response from ${endpoint}:`, response.status, response.statusText);

      // Add more detailed logging about the response
      logger.debug(`Response headers for ${endpoint}:`,
        Object.fromEntries([...response.headers.entries()]));

      // Special handling for 404 on Kafka endpoints to return empty results instead of errors
      if ((response.status === 404 || response.status === 403) && endpoint.includes('kafka')) {
        logger.warn(`${response.status} for Kafka endpoint ${endpoint} - returning empty result instead of error`);

        // Return appropriate empty data structure based on endpoint type
        if (endpoint.includes('/topics')) {
          return { data: [] };
        } else if (endpoint.includes('/spark/jobs')) {
          return { data: [] };
        } else if (endpoint.includes('/cluster/health')) {
          return { status: 'unavailable', brokers: 0, topics: 0, partitions: 0 };
        } else if (endpoint.includes('/schemas')) {
          return {
            data: [],
            status: 'unavailable',
            message: 'Schema Registry is not accessible'
          };
        } else if (endpoint.includes('/schema/')) {
          // Return default schema for topic schema requests
          const topicName = endpoint.split('/').pop();
          return {
            data: {
              id: `${topicName}-value`,
              topic: topicName,
              type: "JSON",
              version: 0,
              schema: "{\n  \"type\": \"object\",\n  \"properties\": {\n    \"message\": { \"type\": \"string\" }\n  }\n}"
            },
            status: 'unavailable',
            message: 'Schema Registry is not accessible, using default schema'
          };
        } else {
          // Generic empty response for other Kafka endpoints
          return { data: [] };
        }
      }

      // Handle 401/403 authentication errors by attempting token refresh
      if (response.status === 401 || response.status === 403) {
        logger.debug('Authentication error detected, attempting token refresh...');
        try {
          // Import authService dynamically to avoid circular dependency
          const { authService } = await import('./authService');
          await authService.refreshToken();

          // Retry the request with refreshed cookie
          logger.debug('Token may have been refreshed, retrying request...');
          const retryResponse = await fetch(`${API_BASE_URL}/${cleanEndpoint}`, {
            signal: controller.signal,
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          return handleResponse(retryResponse);
        } catch (refreshError) {
          logger.error('Token refresh failed:', refreshError);
          // Fall through to original error handling
        }
      }

      return handleResponse(response);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    logger.error(`API GET Error (${endpoint}):`, error);
    logger.error(`Network error details:`, {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    // For timeout errors, provide more specific message and special handling for Schema Registry
    if (error.name === 'AbortError') {
      // Special handling for Schema Registry timeouts
      if (endpoint.includes('kafka/schemas') || endpoint.includes('kafka/schema/')) {
        logger.warn(`Schema Registry request to ${endpoint} timed out - returning default data`);
        // Return appropriate default data instead of throwing an error
        if (endpoint.includes('kafka/schemas')) {
          return { 
            data: [], 
            status: 'unavailable', 
            message: `Schema Registry request timed out after ${options.timeout || 5000}ms`
          };
        } else if (endpoint.includes('kafka/schema/')) {
          // Extract topic name
          const topicName = endpoint.split('/').pop();
          return { 
            data: {
              id: `${topicName}-value`,
              topic: topicName,
              type: "JSON",
              version: 0,
              schema: "{\n  \"type\": \"object\",\n  \"properties\": {\n    \"message\": { \"type\": \"string\" }\n  }\n}"
            },
            status: 'unavailable',
            message: `Schema Registry request timed out after ${options.timeout || 5000}ms`
          };
        }
      }
      
      // Standard timeout handling for other requests
      throw new Error(`Request to ${endpoint} timed out after ${options.timeout || 5000}ms`);
    }
    
    // Add more context to the error for better debugging
    throw new Error(`Failed to fetch from ${endpoint}: ${error.message}`);
  }
};

// Check if backend is reachable
export const checkBackendHealth = async () => {
  try {
    // Try to fetch the root API path
    const response = await fetch(`${API_BASE_URL}/health`);
    if (response.ok) {
      return { isHealthy: true, message: "Backend is reachable" };
    }
    return { 
      isHealthy: false, 
      status: response.status,
      message: `Backend responded with status: ${response.status} ${response.statusText}` 
    };
  } catch (error) {
    return { 
      isHealthy: false, 
      message: "Backend connection failed. Make sure the backend server is running and accessible."
    };
  }
};

export const apiPost = async (endpoint, data, options = {}) => {
  // Set a timeout for request - for Kafka messaging and optimization endpoints, use a longer timeout
  let defaultTimeout = 30000; // 30 seconds default timeout for most endpoints

  // For Kafka messaging endpoints, use a timeout that aligns with backend (45 seconds)
  if (endpoint.includes('kafka/messaging')) {
    defaultTimeout = 45000; // 45 seconds for Kafka messaging endpoints (backend times out at 30s + buffer)
    logger.debug(`Using extended timeout (${defaultTimeout}ms) for Kafka messaging endpoint: ${endpoint}`);
  }

  // For optimization endpoints, use a longer timeout (60 seconds) as analysis can take 20+ seconds
  if (endpoint.includes('optimization/pipelines') && endpoint.includes('analyze')) {
    defaultTimeout = 60000; // 60 seconds for optimization analysis endpoints
    logger.debug(`Using extended timeout (${defaultTimeout}ms) for optimization analysis endpoint: ${endpoint}`);
  }

  // For pipeline start/execution endpoints, use a longer timeout (5 minutes) as file processing can take time
  if (endpoint.includes('pipelines/') && endpoint.includes('/start')) {
    defaultTimeout = 300000; // 5 minutes for pipeline execution (file processing, Kafka messages)
    logger.debug(`Using extended timeout (${defaultTimeout}ms) for pipeline start endpoint: ${endpoint}`);
  }

  // For SQL query endpoints, use extended timeout (6 hours) for large dataset operations (1B+ rows)
  if (endpoint.startsWith('sql/') && (endpoint.includes('query') || endpoint.includes('unified'))) {
    defaultTimeout = 21600000; // 6 hours for SQL operations on very large datasets
    logger.debug(`Using extended timeout (${defaultTimeout}ms) for SQL query endpoint: ${endpoint}`);
  }

  const timeout = options.timeout || defaultTimeout;
  const controller = options.signal ? null : new AbortController();
  const timeoutId = controller ? setTimeout(() => {
    logger.debug(`Request to ${endpoint} timed out after ${timeout}ms`);
    controller.abort();
  }, timeout) : null;
  
  try {
    // Remove leading slash from endpoint if it exists to prevent double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    logger.debug(`Making POST request to: ${API_BASE_URL}/${cleanEndpoint}`);
    
    // Special handling for pipelines endpoint
    if (endpoint === 'pipelines' && data && data.transformations) {
      logger.debug('Pipeline transformations before serialization:', data.transformations);
      
      // Add special checks for transformation structure
      data.transformations.forEach((t, i) => {
        if (!t.type) {
          logger.error(`Transformation at index ${i} missing type property:`, t);
        }
        
        // Check for required properties based on type
        if (t.type === 'filter' && (!t.conditions || !Array.isArray(t.conditions))) {
          logger.error(`Filter transformation missing conditions array:`, t);
        }
        if (t.type === 'sort' && (!t.sortFields || !Array.isArray(t.sortFields))) {
          logger.error(`Sort transformation missing sortFields array:`, t);
        }
        if (t.type === 'groupBy' && (!t.groupByFields || !Array.isArray(t.groupByFields))) {
          logger.error(`GroupBy transformation missing groupByFields array:`, t);
        }
        if (t.type === 'transform' && (!t.fieldMappings || typeof t.fieldMappings !== 'object')) {
          logger.error(`Transform transformation missing fieldMappings object:`, t);
        }
      });
    }
    
    // Stringify with better error handling
    let jsonBody;
    try {
      jsonBody = JSON.stringify(data);
      // Only log a portion of the JSON string if it exists and has content
      if (jsonBody && typeof jsonBody === 'string') {
        const previewLength = Math.min(jsonBody.length, 500);
        logger.debug(`Request body (first ${previewLength} chars): ${jsonBody.substring(0, previewLength)}${jsonBody.length > 500 ? '...' : ''}`);
      } else {
        logger.debug('Request body: empty or invalid');
      }
    } catch (err) {
      logger.error('JSON serialization error:', err);
      throw new Error(`Failed to serialize request data: ${err.message}`);
    }
    
    // Check if endpoint is for kafka integrations and add special handling
    const isKafkaIntegration = endpoint.includes('kafka/integrations');
    if (isKafkaIntegration) {
      logger.debug('Making Kafka integration request with special handling');
      
      // For Kafka integration endpoints, prepare custom headers
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Connection': 'keep-alive',
        // Add custom headers to help with CORS
        'X-Requested-With': 'XMLHttpRequest',
        'Origin': window.location.origin
      };
      
      // Include any custom headers from options
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          headers[key] = options.headers[key];
        });
      }
      
      const fetchOptions = {
        method: 'POST',
        headers: headers,
        body: jsonBody,
        signal: options.signal || (controller ? controller.signal : null),
        keepalive: true,
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'include'  // Include httpOnly cookies
      };
      
      logger.debug(`Kafka integration request with options:`, fetchOptions);
      
      // Make the request with these special options
      const response = await fetch(`${API_BASE_URL}/${cleanEndpoint}`, fetchOptions);
      
      // Process the response instead of returning the raw Response object
      logger.debug(`Received Kafka integration response from ${endpoint}:`, response.status, response.statusText);
      return handleResponse(response);
    }
    
    // For non-Kafka integration endpoints, use standard settings
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Connection': 'keep-alive'
    };

    // Include any custom headers from options
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        headers[key] = options.headers[key];
      });
    }

    const response = await fetch(`${API_BASE_URL}/${cleanEndpoint}`, {
      method: 'POST',
      headers: headers,
      body: jsonBody,
      signal: options.signal || (controller ? controller.signal : null),
      credentials: 'include',  // Include httpOnly cookies
      keepalive: true
    });

    logger.debug(`Received response from ${endpoint}:`, response.status, response.statusText);

    // For Kafka integration endpoints, add special handling for common errors
    if (isKafkaIntegration && !response.ok) {
      if (response.status === 404) {
        throw new Error(`Kafka integration endpoint not found. Please check the API configuration.`);
      } else if (response.status === 405) {
        throw new Error(`Method not allowed for Kafka integration. The endpoint might only support GET requests.`);
      }
    }

    return handleResponse(response);
  } catch (error) {
    logger.error(`API POST Error (${endpoint}):`, error);
    if (error.name === 'AbortError') {
      throw new Error(`Request to ${endpoint} timed out after ${timeout}ms. The server might be busy or unreachable.`);
    }
    throw error; // Preserve the original error for better debugging
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export const apiPut = async (endpoint, data, options = {}) => {
  // Set a longer timeout for request
  const controller = options.signal ? null : new AbortController();
  const timeoutId = controller ? setTimeout(() => controller.abort(), options.timeout || 30000) : null;
  try {
    // Remove leading slash from endpoint if it exists to prevent double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    logger.debug(`Making PUT request to: ${API_BASE_URL}/${cleanEndpoint}`);
    
    // Validate the data to ensure we're sending a valid body
    if (!data) {
      throw new Error(`Cannot send PUT request to ${endpoint} with empty data`);
    }
    
    // Stringify the data with proper error handling
    let jsonData;
    try {
      jsonData = JSON.stringify(data);
      logger.debug(`Request payload for ${endpoint}:`, jsonData.substring(0, 500));
    } catch (err) {
      throw new Error(`Failed to stringify data for ${endpoint}: ${err.message}`);
    }
    
    // Prepare headers with default and custom options
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Connection': 'keep-alive',
      'X-Requested-With': 'XMLHttpRequest'
    };
    
    // Add any custom headers from options
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        headers[key] = options.headers[key];
      });
    }
    
    // Configure CORS mode from options
    const corsMode = options.mode || 'cors';
    const credentials = options.credentials || 'same-origin';
    
    logger.debug(`Making PUT request with CORS mode: ${corsMode}, credentials: ${credentials}`);
    
    // Make the fetch request with configured options
    const response = await fetch(`${API_BASE_URL}/${cleanEndpoint}`, {
      method: 'PUT',
      headers: headers,
      body: jsonData,
      signal: options.signal || (controller ? controller.signal : null),
      keepalive: true,
      mode: corsMode,
      credentials: 'include',  // Always include httpOnly cookies
      cache: 'no-cache',
      redirect: 'follow'
    });
    
    logger.debug(`Received response from ${endpoint}:`, response.status, response.statusText);
    logger.debug(`Response headers:`, Object.fromEntries([...response.headers.entries()]));
    
    if (endpoint.includes('topics') && endpoint.includes('retention')) {
      logger.debug('Handling special case for topic retention updates');
      
      if (response.status === 403) {
        logger.error('Received 403 Forbidden for topic retention update');
        return { success: false, error: 'Permission denied' };
      }
      
      if (response.ok) {
        if (response.status === 204 || response.headers.get('content-length') === '0') {
          logger.debug('Received empty but successful response');
          return { success: true };
        }
      }
    }
    
    return handleResponse(response);
  } catch (error) {
    logger.error(`API PUT Error (${endpoint}):`, error);
    if (error.name === 'AbortError') {
      throw new Error(`Request to ${endpoint} timed out. The server might be busy.`);
    }
    throw new Error(`Failed to put to ${endpoint}: ${error.message}`);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export const apiDelete = async (endpoint) => {
  try {
    // Remove leading slash from endpoint if it exists to prevent double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    logger.debug(`Making DELETE request to: ${API_BASE_URL}/${cleanEndpoint}`);
    const response = await fetch(`${API_BASE_URL}/${cleanEndpoint}`, {
      method: 'DELETE',
      credentials: 'include',  // Include httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    logger.debug(`Received response from ${endpoint}:`, response.status, response.statusText);
    return handleResponse(response);
  } catch (error) {
    logger.error(`API DELETE Error (${endpoint}):`, error);
    throw new Error(`Failed to delete from ${endpoint}: ${error.message}`);
  }
};